import { useLazyQuery } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import { DEBOUNCE_TIME_FAST } from "~/common/constants";
import {
  GetUsersForTenantMemberSelectDocument,
  type GetUsersForTenantMemberSelectQuery,
  type GetUsersForTenantMemberSelectQueryVariables,
} from "~/features/admin/tenants/gqls";
import type { FilterUserOption } from "~/features/admin/tenants/types";

interface UseTenantUserSearchOptions {
  /** Restrict results to a single organization (member picker). */
  organizationId?: string;
  /** Exclude users already selected / already members. */
  excludeIds?: string[];
}

/**
 * Debounced async user search shared by the member picker and the user filter.
 * Returns the current `options`, a `loading` flag, and `search` / `reset`.
 */
export function useTenantUserSearch({ organizationId, excludeIds = [] }: UseTenantUserSearchOptions = {}) {
  const [options, setOptions] = useState<FilterUserOption[]>([]);
  const [getUsers, { loading }] = useLazyQuery<
    GetUsersForTenantMemberSelectQuery,
    GetUsersForTenantMemberSelectQueryVariables
  >(GetUsersForTenantMemberSelectDocument);

  const search = useDebouncedCallback((value: string) => {
    if (!value) {
      setOptions([]);
      return;
    }
    void getUsers({
      variables: {
        where: {
          _and: [
            ...(organizationId ? [{ organizationId: { _eq: organizationId } }] : []),
            { _or: [{ email: { _ilike: `%${value}%` } }, { displayName: { _ilike: `%${value}%` } }] },
            ...(excludeIds.length > 0 ? [{ id: { _nin: excludeIds } }] : []),
          ],
        },
      },
    }).then(({ data }) => {
      setOptions(
        (data?.users ?? []).map((u) => ({
          id: u.id,
          displayName: u.displayName ?? null,
          email: u.email,
          deactivatedAt: u.deactivatedAt ?? null,
        })),
      );
    });
  }, DEBOUNCE_TIME_FAST);

  const reset = useCallback(() => setOptions([]), []);

  return { options, loading, search, reset };
}
